import { Avatar, CardActionArea, CircularProgress, Divider, makeStyles, Theme } from '@material-ui/core';
import MuiCloseIcon from '@material-ui/icons/Close';
import classNames from 'classnames';
import React from 'react';

import {
  NotificationActivityFragment,
  useDeleteNotificationActivityMutation,
  useGetLoggedInUserQuery,
  useSeenNotificationActivityMutation,
} from '../../../../graphql/gen/types';
import { colorPalette, spacings, TextSizesEnum } from '../../../../theme';
import { ButtonColor } from '../../../shared/primitives/PlexButton';
import { PlexIconButton } from '../../../shared/primitives/PlexIconButton';
import { PlexTypography } from '../../../shared/primitives/PlexTypography';
import { PlexName } from '../../common/PlexName';
import { PostDate } from '../../common/PostDate';
import { getActionText, getTargetName, useGetOnClickChangeEvent } from './utils';

interface Props {
  notification: NotificationActivityFragment;
}

export const NotificationItem = React.memo(({ notification }: Props): React.ReactElement | null => {
  const classes = useStyles();
  const [seenNotification] = useSeenNotificationActivityMutation();
  const eventOnClick = useGetOnClickChangeEvent(notification.event);
  const [deleteNotificationMutation, { loading }] = useDeleteNotificationActivityMutation();
  const loggedInUser = useGetLoggedInUserQuery({ variables: {} });
  const { event } = notification;

  return (
    <React.Fragment>
      <CardActionArea
        className={classNames(classes.root, !notification.isRead && classes.rootUnread)}
        onClick={(): void => {
          if (!!eventOnClick) {
            eventOnClick();
          }
          seenNotification({ variables: { id: notification.id } });
        }}
      >
        <Avatar className={classes.avatar} src={event.author.picture} alt={event.author.name} />
        <div className={classes.content}>
          <div className={classes.header}>
            <PlexTypography className={classes.authorName} text={event.author.name} size={TextSizesEnum.Normal} />
            <PostDate
              className={classes.eventDate}
              prefix={'• '}
              size={TextSizesEnum.Normal}
              date={new Date(event.createdAt)}
            />
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              <PlexIconButton
                className={classes.deleteButton}
                MuiIcon={MuiCloseIcon}
                color={ButtonColor.TransparentBlack}
                tooltip={'Delete notification'}
                onClick={(event: React.MouseEvent<HTMLElement, MouseEvent>): void => {
                  deleteNotificationMutation({ variables: { notificationActivityId: notification.id } });
                }}
                isOverButton={true}
              />
            )}
          </div>

          <div className={classes.eventActionContainer}>
            <PlexTypography
              className={classes.eventActionText}
              text={`${getActionText(event, loggedInUser.data?.currentUser?.email)} in `}
              size={TextSizesEnum.Normal}
            />
            <PlexName className={classes.eventPlex} name={event.plex.name} color={event.plex.color} />
          </div>

          <PlexTypography text={getTargetName(event)} size={TextSizesEnum.Normal} className={classes.eventText} />
        </div>
      </CardActionArea>

      <Divider />
    </React.Fragment>
  );
});

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    paddingRight: spacings.normal,
    cursor: 'pointer',
    padding: spacings.normal,
    backgroundColor: '#fff',
  },
  rootUnread: {
    backgroundColor: '#f7f7f7',
  },
  avatar: {
    display: 'flex',
    marginRight: spacings.small,
    width: 40,
    height: 40,
  },
  content: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    marginLeft: spacings.normal,
  },
  header: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  authorName: {
    color: colorPalette.purpleGray._500,
    fontWeight: 'bold',
  },
  eventDate: {
    color: colorPalette.purpleGray._400,
  },
  deleteButton: {
    marginLeft: 'auto',
  },
  eventActionText: {
    color: colorPalette.purpleGray._500,
  },
  eventText: {
    color: colorPalette.dark._900,
    marginTop: spacings.normal,
  },
  eventPlex: {
    marginLeft: spacings.small,
  },
  eventActionContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: spacings.xsmall,
  },
}));
